import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  TableContainer,
  SxProps,
  Theme,
  Box,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  formatNumber,
  shortenAddress,
} from '../../../../shared/utils/Formatter';
import { SelectedValidator, ValidatorUserSocial } from '../../../../interfaces';
import EmptyState from '../../../../components/common/EmptyState';
import { readContract } from 'wagmi/actions';
import { config } from '../../../../shared/constants/WagmiConfig';
import { ethers } from 'ethers';
import { personaTokenABI } from '../../../../assets/abis/personaToken';

declare global {
  interface BigInt {
    toJSON(): string;
  }
}

BigInt.prototype.toJSON = function () {
  return this.toString();
};

interface Column {
  id: 'validator' | 'delegated';
  label: string;
  render: (value: any) => {};
  align?: 'right' | 'left';
}

interface ValidatorProfile {
  profilePhoto: string;
  username: string;
  walletAddress: string;
  participationRate: number;
}

interface ValidatorData {
  id: number;
  validator: ValidatorProfile;
  delegated: BigInt;
}

const profilePhotoStyle: React.CSSProperties = {
  width: '32px',
  height: '32px',
  borderRadius: '50%',
};

const validatorProfileStyle: SxProps<Theme> = {
  display: 'flex',
  gap: 1,
  alignItems: 'center',
};

interface ValidatorTableProps {
  data: ValidatorUserSocial[] | undefined;
  tokenAddress: string;
  userAddress: string;
  totalDelegate: BigInt;
  onRowClick: (withdrawAmount: BigInt) => void;
}

const ValidatorTable: React.FC<ValidatorTableProps> = (props) => {
  const [validators, setValidators] = useState<ValidatorData[]>([]);

  useEffect(() => {
    if (props.data && props.data.length > 0) {
      let data: ValidatorData[] = props.data.map((d) => ({
        id: d.id,
        validator: {
          profilePhoto:
            d.userSocials.length > 0
              ? `https://effigy.im/a/${d.userSocials[0].walletAddress}.svg`
              : '',
          username: d.username,
          walletAddress:
            d.userSocials.length > 0 ? d.userSocials[0].walletAddress : '',
          participationRate: d.participationRate || 0,
        },
        delegated: props.totalDelegate,
      }));

      if (data && data.length === 0) return;

      setValidators(data);
    }
  }, [props.data]);

  const headerColumns: readonly Column[] = [
    {
      id: 'validator',
      label: 'Validator',
      render: (value: ValidatorProfile) => {
        return (
          <Box sx={validatorProfileStyle}>
            <img
              src={value.profilePhoto}
              alt="profile"
              style={profilePhotoStyle}
            />
            <Typography>{shortenAddress(value.walletAddress)}</Typography>
          </Box>
        );
      },
    },
    {
      id: 'delegated',
      label: 'Delegated',
      render: (value) => {
        return <Typography>{Number(ethers.formatEther(value))} $xV</Typography>;
      },
      align: 'right',
    },
  ];

  return (
    <TableContainer sx={{ maxHeight: 500 }}>
      <Table stickyHeader aria-label="sticky table">
        <TableHead>
          <TableRow>
            {headerColumns.map((column) => (
              <TableCell align={column.align} key={column.id}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {validators?.length === 0 && (
            <TableCell colSpan={headerColumns.length}>
              <EmptyState header="No validator found" />
            </TableCell>
          )}
          {validators?.map((row, index) => (
            <TableRow
              hover
              role="checkbox"
              tabIndex={-1}
              key={index}
              onClick={() => props.onRowClick(row.delegated)}
            >
              {headerColumns.map((column, colIndex) => {
                return (
                  <TableCell align={column.align} key={`${index}-${colIndex}`}>
                    <>{column.render(row[column.id])}</>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ValidatorTable;
